import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw, Search, Link2, X as XIcon, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { ticketAlertsAPI, adminCwMemberAPI } from '../../../services/api';

function MatchBadge({ source, cwMemberId }) {
  if (!cwMemberId) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
        <AlertCircle className="h-3 w-3" /> Not linked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900/30 dark:text-green-300">
      <CheckCircle className="h-3 w-3" /> {source === 'manual' ? 'Manual' : 'Auto (email)'}
    </span>
  );
}

function MemberSearch({ onPick, onCancel }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (search.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await adminCwMemberAPI.searchMembers(search.trim());
        setResults(resp.data?.items || []);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="w-72">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-neutral-400" />
        <input autoFocus type="text" className="input pl-8 pr-8" placeholder="Search CW members…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button type="button" onClick={onCancel} className="absolute right-2 top-2.5 text-neutral-400 hover:text-neutral-600">
          <XIcon className="h-4 w-4" />
        </button>
      </div>
      {(searching || results.length > 0) && (
        <ul className="mt-1 max-h-48 overflow-auto rounded-lg border border-neutral-200 bg-white text-sm shadow dark:border-neutral-700 dark:bg-neutral-800">
          {searching && (
            <li className="flex items-center gap-2 px-3 py-2 text-neutral-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
            </li>
          )}
          {results.map((m) => {
            const name = m.name || `${m.firstName || ''} ${m.lastName || ''}`.trim() || m.identifier;
            return (
              <li key={m.id}>
                <button type="button" onClick={() => onPick({ id: m.id, name })} className="flex w-full flex-col items-start px-3 py-2 text-left hover:bg-neutral-50 dark:hover:bg-neutral-700">
                  <span className="text-neutral-900 dark:text-white">{name}</span>
                  <span className="text-xs text-neutral-500">
                    {m.identifier}
                    {m.email ? ` · ${m.email}` : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const PLATFORM_LABELS = { slack: 'Slack', gchat: 'Google Chat', teams: 'Microsoft Teams' };

/**
 * Chat user → ConnectWise member mapping, per connected platform. Auto-matched
 * by email; each row can be overridden. Unlinked users can view cards but
 * cannot "Take it" or be assigned.
 */
export default function TechnicianMappingTable({ connectedPlatforms = [] }) {
  const queryClient = useQueryClient();
  const [platform, setPlatform] = useState(connectedPlatforms[0] || 'slack');
  const [editing, setEditing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!connectedPlatforms.includes(platform) && connectedPlatforms.length > 0) setPlatform(connectedPlatforms[0]);
  }, [connectedPlatforms, platform]);

  const connected = connectedPlatforms.includes(platform);
  const { data, isLoading, error } = useQuery({
    queryKey: ['ticket-alerts-members', platform],
    queryFn: () => ticketAlertsAPI.members(false, platform),
    enabled: connected,
    select: (resp) => resp.data || [],
  });

  const refresh = async () => {
    setRefreshing(true);
    try {
      await ticketAlertsAPI.members(true, platform);
      await queryClient.invalidateQueries({ queryKey: ['ticket-alerts-members', platform] });
    } finally {
      setRefreshing(false);
    }
  };

  const inviteMutation = useMutation({
    mutationFn: () => ticketAlertsAPI.inviteGchatTechnicians(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ticket-alerts-members', platform] }),
  });

  const mapMutation = useMutation({
    mutationFn: ({ externalUserId, cwMemberId, cwMemberName }) => ticketAlertsAPI.setMemberMapping(externalUserId, { cwMemberId, cwMemberName }, platform),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['ticket-alerts-members', platform] });
    },
  });

  if (connectedPlatforms.length === 0) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">Connect a chat platform (step 2) to load its members.</p>;
  }

  const rows = data || [];
  const linked = rows.filter((r) => r.cwMemberId).length;

  return (
    <div className="space-y-3">
      {connectedPlatforms.length > 1 && (
        <div className="inline-flex rounded-lg border border-neutral-200 p-0.5 dark:border-neutral-700">
          {connectedPlatforms.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPlatform(p)}
              className={`rounded-md px-3 py-1 text-sm ${platform === p ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-700'}`}
            >
              {PLATFORM_LABELS[p] || p}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {isLoading ? 'Loading…' : `${linked} of ${rows.length} ${PLATFORM_LABELS[platform]} users linked to a ConnectWise member.`}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {platform === 'gchat' && (
            <button
              type="button"
              onClick={() => inviteMutation.mutate()}
              disabled={inviteMutation.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
            >
              {inviteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Ask unlinked people to link
            </button>
          )}
          <button
            type="button"
            onClick={refresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-200 dark:hover:bg-neutral-700"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            {rows.length === 0 ? `Load ${PLATFORM_LABELS[platform]} users & auto-match` : 'Re-run auto-match'}
          </button>
        </div>
      </div>
      {inviteMutation.isSuccess && (
        <p className="text-sm text-green-600 dark:text-green-400">
          {inviteMutation.data?.data?.results?.some((r) => r.posted)
            ? 'Posted a prompt in the space. People who press "Link my account" are matched instantly.'
            : 'Nothing to send — everyone in the configured spaces is already linked.'}
        </p>
      )}
      {inviteMutation.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">{inviteMutation.error?.response?.data?.error || 'Could not post the prompt.'}</p>
      )}
      {platform === 'gchat' && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Google withholds member email addresses from apps, so these start unlinked. Google does send the email with every button press, so anyone who uses a ticket card is matched automatically. To prompt the rest, use <strong>Ask unlinked people to link</strong> — it posts a message in the space mentioning them with a one-press button. An app cannot open a direct message first, so direct messages (Follow, on-call) still need each person to message the app once.
        </p>
      )}

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error.response?.data?.error || error.message}</p>}
      {mapMutation.isError && <p className="text-sm text-red-600 dark:text-red-400">{mapMutation.error?.response?.data?.error || 'Update failed'}</p>}

      {rows.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 dark:border-neutral-700">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase text-neutral-500 dark:bg-neutral-900/40 dark:text-neutral-400">
              <tr>
                <th className="px-4 py-2">{PLATFORM_LABELS[platform]} user</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">ConnectWise member</th>
                <th className="px-4 py-2">Match</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {rows.map((r) => (
                <tr key={r.externalUserId}>
                  <td className="px-4 py-2 text-neutral-900 dark:text-white">{r.displayName || r.externalUserId}</td>
                  <td className="px-4 py-2 text-neutral-500 dark:text-neutral-400">{r.email || '—'}</td>
                  <td className="px-4 py-2">
                    {editing === r.externalUserId ? (
                      <MemberSearch onCancel={() => setEditing(null)} onPick={(m) => mapMutation.mutate({ externalUserId: r.externalUserId, cwMemberId: m.id, cwMemberName: m.name })} />
                    ) : r.cwMemberId ? (
                      <span className="inline-flex items-center gap-1 text-neutral-900 dark:text-white">
                        <Link2 className="h-3.5 w-3.5 text-green-600" /> {r.cwMemberName || `#${r.cwMemberId}`}
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <MatchBadge source={r.matchSource} cwMemberId={r.cwMemberId} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {editing !== r.externalUserId && (
                      <>
                        <button type="button" onClick={() => setEditing(r.externalUserId)} className="text-xs text-primary-600 hover:underline dark:text-primary-400">
                          {r.cwMemberId ? 'Change' : 'Link'}
                        </button>
                        {r.cwMemberId && (
                          <button
                            type="button"
                            onClick={() => mapMutation.mutate({ externalUserId: r.externalUserId, cwMemberId: null, cwMemberName: null })}
                            className="ml-3 text-xs text-neutral-500 hover:text-red-600"
                          >
                            Unlink
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
